import BaseObjectDirective from './BaseObjectDirective';

/**
 * AsfImageObjectCtrl is a controller class that extends BaseObjectDirective.
 * It manages the behavior and state of image objects within the application.
 */
export class AsfImageObjectCtrl extends BaseObjectDirective {
    /**
     * Constructor for AsfImageObjectCtrl.
     * @param {Object} $injector - AngularJS injector service.
     */
    constructor($injector, $scope) {
        super($injector, $scope);

        this.$uibModal = $injector.get('$uibModal');
        this.TaskSelectionService = $injector.get('TaskSelectionService');
        this.$q = $injector.get('$q');
        this.SheetLoadedService = $injector.get('SheetLoadedService');

        this.$scope.isStimulus = true;
        this.initializeImageObject();
    }

    initializeImageObject() {
        this.$scope.isCMS = this.isCMS;
        this.$scope.prefsMode = false;
        this.$scope.hasBrowseImage = true;
        this.$scope.hasUploadImage = false;
        this.$scope.selectable = true;

        this.$scope.taskuuid = this.TaskSelectionService.getTaskUUID();
        this.$scope.mediaObject = this.$scope.contentObject.getContentReference(false) || false;

        this.$scope.imageSizes = [
            'Original',
            'Full',
            '75%',
            '50%',
            '25%'
        ];

        this.$scope.mediaURL = this.getMediaURL(this.$scope.mediaObject);
        this.initPreferences();

        this.$scope.$on('$destroy', () => {
            this.$signals.remove(IB.Signals.CMS_MEDIA_OBJECT_SELECTED, this.onSelectMedia);
        });

        this.$signals.on(IB.Signals.CMS_MEDIA_OBJECT_SELECTED, this.onSelectMedia.bind(this));

        if (!this.$scope.inOverview) {
            this.setupSheetLoadedService();
        }
    }

    setupSheetLoadedService() {
        const _d = this.$q.defer();
        this.SheetLoadedService.wait(_d.promise, 'image');
        const self = this;
        this.$timeout(() => {
            const $images = self.$scope.$parent.element ? self.$scope.$parent.element.find('img') : $();
            if ($images.length > 0) {
                $images
                    .one("load", () => {
                        _d.resolve('image');
                    })
                    .one("error", function () {
                        console.warn('[ImageObjectDirective] Image failed to load, resolving sheet anyway');
                        _d.resolve('image');

                        const imgEl = this;
                        const originalSrc = imgEl.src;
                        const retryTimer = setTimeout(() => {
                            imgEl.src = '';
                            imgEl.src = originalSrc;
                        }, 2000);

                        self.$scope.$on('$destroy', () => clearTimeout(retryTimer));
                    })
                    .each(function () {
                        if (this.complete) {
                            if (this.naturalWidth > 0) {
                                $(this).trigger('load');
                            } else {
                                $(this).trigger('error');
                            }
                        }
                    });
            } else {
                _d.resolve('image');
            }
        });
    }

    /**
     * Generates the media URL for the given media object.
     * @param {Object} mediaObject - The media object for which to generate the URL.
     * @param {boolean} [thumbnailParam] - Optional parameter to indicate if a thumbnail is requested.
     * @returns {string|boolean} - The generated URL or false if the mediaObject is not an object.
     */
    getMediaURL(mediaObject, thumbnailParam) {
        const thumbnailSlug = '_thumb';
        const thumbnailExtension = 'jpg';

        if (!angular.isObject(mediaObject)) {
            return false;
        }

        const thumbnail = angular.isDefined(thumbnailParam) ? thumbnailParam : false;
        const re = /(?:\.([^.]+))?$/;
        const uploadName = mediaObject.getUploadName();
        const extension = thumbnail ? thumbnailExtension : re.exec(uploadName)[1];
        const mimeType = mediaObject.getMimeType();
        const fileNameSlug = thumbnail ? thumbnailSlug : "";
        let fileName = mediaObject.getUUID() + fileNameSlug + '.' + extension;

        if (mimeType == 'image/svg+xml') {
            fileName = mediaObject.getUUID() + fileNameSlug + '.svg';
        }

        const url = this.$scope.isCMS ? "/media/" + this.$scope.taskuuid + "/" + fileName : "/media/" + fileName;
        return url;
    }

    /**
     * Retrieves the alternate text for the image.
     * @returns {string} - The alternate text.
     */
    getAlternateText() {
        const altText = (typeof this.$scope.contentObject.getPreferences() != "undefined" ? this.$scope.contentObject.getPreferences().altText : "");
        return altText;
    }

    /**
     * Deletes the overview image.
     */
    deleteOverviewImage() {
        if (this.$scope.sheet) {
            this.$scope.$emit('deleteOverviewImage', {
                'sheet': this.$scope.sheet,
                'contentObject': this.$scope.contentObject
            });
        }
    }

    /**
     * Gets the image size based on the type requested.
     * @param {string} type - The type of size to retrieve ('height' or 'width').
     * @returns {string} - The size in pixels or percentage.
     */
    getImageSize(type) {
        let height = 0, width = 0;
        const prefDisplaySize = this.$scope.contentObject.getPreferences().displaySize || undefined;

        if (!angular.isDefined(prefDisplaySize) || prefDisplaySize == 'Original') {
            if (this.$scope.mediaObject) {
                width = this.$scope.mediaObject.getWidth() + "px";
                height = this.$scope.mediaObject.getHeight() + "px";
            }
        } else if (prefDisplaySize != 'Original' && prefDisplaySize != 'Full') {
            width = prefDisplaySize;
            height = prefDisplaySize;
        } else if (prefDisplaySize == 'Full') {
            width = '100%';
            height = '100%';
        }

        if (width === 0) {
            width = '100%';
        }

        if (height === 0) {
            height = '100%';
        }

        return type == 'height' ? height : width;
    }

    /**
     * Resets the changes made to the image.
     */
    resetChanges() {
        const _tmp = this.$scope.resetChanges;
        _tmp();
        console.log("override # <" + this.$scope.contentObject.getUUID() + "> resetChanges()");
        this.initPreferences();
    }

    /**
     * Initializes the preferences for the image.
     */
    initPreferences() {
        const altText = (typeof this.$scope.contentObject.getPreferences() != "undefined" ? this.$scope.contentObject.getPreferences().altText : null);
        const displaySize = (typeof this.$scope.contentObject.getPreferences() != "undefined" ? this.$scope.contentObject.getPreferences().displaySize : undefined);
        const maximizable = (typeof this.$scope.contentObject.getPreferences() != "undefined" ? this.$scope.contentObject.getPreferences().maximizable : null);
        const includedInPDF = (typeof this.$scope.contentObject.getPreferences() !== "undefined" ? this.$scope.contentObject.getPreferences().includedInPDF : false);
        const targetHintObject = (typeof this.$scope.contentObject.getPreferences() != "undefined" ? this.$scope.contentObject.getPreferences().targetHintObject : undefined);

        this.$scope.$watch('preferencesValues', (val1, val2) => {
            if (val1 == val2) return;
            const preferences = {};
            angular.forEach(this.$scope.preferencesValues, (value, key) => {
                preferences[key] = value;
            });
            this.$scope.contentObject.setPreferences(preferences);
        }, true);

        this.$scope.preferencesValues = {};
        this.$scope.preferencesValues['altText'] = altText;
        this.$scope.preferencesValues['displaySize'] = displaySize;
        this.$scope.preferencesValues['maximizable'] = maximizable;
        this.$scope.preferencesValues['includedInPDF'] = typeof includedInPDF !== 'undefined' ? includedInPDF : false;
        this.$scope.preferencesValues['targetHintObject'] = targetHintObject;

        this.$scope.preferences = [
            {
                'maximizable': [
                    { 'type': 'checkbox', 'name': 'Maximizable?', 'valueKey': 'maximizable' }
                ],
                'alttext': [
                    { 'type': 'input', 'name': 'Alternative Text', 'valueKey': 'altText' }
                ],
                'displaySize': [
                    { 'type': 'select', 'name': 'Display Size:', 'options': this.$scope.imageSizes, 'valueKey': 'displaySize' }
                ],
                'includedInPDF': [
                    { 'type': 'checkbox', 'name': 'Included in Response PDF', 'valueKey': 'includedInPDF' }
                ],
                'targetHintObject': [{
                    'type': 'select-other-content-object',
                    'name': ' link this Object as Hint',
                    'valueKey': 'targetHintObject',
                    'objectType': 'hint',
                    'parentContainer': this.$scope.$parent.containerObj
                }]
            },
        ];

        if (this.$scope.mode === 'overview') {
            delete this.$scope.preferences['maximizable'];
            delete this.$scope.preferences['displaySize'];
            delete this.$scope.preferences['includedInPDF'];
            delete this.$scope.preferences['targetHintObject'];
        }
    }

    /**
     * Handles the media browse button click event.
     */
    onMediaBrowseButtonClick() {
        const modalInstance = this.$uibModal.open({
            templateUrl: '/views/ib/cms/eassessment/modal/eassessmentmediabrowserModal.html',
            scope: this.$scope,
            controller: 'browseModalInstanceController',
            resolve: {
                'contentObjectUUID': () => {
                    return this.$scope.contentObject.getUUID();
                }
            }
        });
    }

    /**
     * Handles the selection of a media object.
     * @param {Object} selectedMedia - The selected media object.
     */
    onSelectMedia(selectedMedia) {
        if (selectedMedia.contentobjectuuid == this.$scope.contentObject.getUUID()) {
            this.$scope.contentObject.setContentReference(selectedMedia.uuid);
            const mediaObject = this.$scope.contentObject.getContentReference();
            this.$scope.mediaObject = mediaObject;
            this.$scope.mediaURL = this.getMediaURL(mediaObject);
            if (modalInstance !== null) {
                modalInstance.close();
            }
        }
    }

    /**
     * Gets the image width in PDF.
     * @returns {number} - The width of the image in PDF.
     */
    getImageWidthInPDF() {
        this.$scope.contentObject.getPreferences().displaySize = this.$scope.contentObject.getPreferences().displaySize === 'Original' ? "100" : this.$scope.contentObject.getPreferences().displaySize;
        const width = ($(".pdf-image").innerWidth() * (parseInt(this.$scope.contentObject.getPreferences().displaySize.replace(/%/g, '')) / 100));
        return width > 1020 ? 1020 : width;
    }

    /**
     * Gets the image height in PDF.
     * @returns {number} - The height of the image in PDF.
     */
    getImageHeightInPDF() {
        this.$scope.contentObject.getPreferences().displaySize = this.$scope.contentObject.getPreferences().displaySize === 'Original' ? "100" : this.$scope.contentObject.getPreferences().displaySize;
        const d = this.$scope.mediaObject.getHeight() / this.$scope.mediaObject.getWidth();
        return $(".pdf-image").innerWidth() * d * (parseInt(this.$scope.contentObject.getPreferences().displaySize.replace(/%/g, '')) / 100);
    }

    /**
     * Checks if the current view is a carousel tab.
     * @returns {boolean} - True if the view is a carousel tab, false otherwise.
     */
    isCarouselTab() {
        return this.$scope.$parent.containerObj.isShowCarousels() || this.$scope.$parent.containerObj.isShowTabs();
    }
}

/**
 * Registers the AsfImageObject directive with the given module.
 * @param {Object} module - The AngularJS module to register the directive with.
 */
export default function registerAsfImageObjectDirective(module) {
    module.directive('asfImageObject', function ($injector) {
        return {
            restrict: 'EA',
            replace: true,
            scope: {
                "contentObject": "=",
                "prefsBar": "@",
                "mode": "@",
                "sheet": "=",
                "inOverview": "="
            },
            templateUrl: "/views/ib/contentobjects/image.html",
            controller: AsfImageObjectCtrl
        };
    });

    module.run(['ContentObjectRegistryService', function (ContentObjectRegistryService) {
        const obj = [{
            "uuid": "",
            "content": "",
            "objectType": "image",
            "type": "contentObject",
            "contentReference": false,
            "preferences": {
                "altText": "",
                "maximizable": false,
                "displaySize": "Original",
                "includedInPDF": false
            }
        }];

        const objProperties = {
            type: "image",
            label: 'CMS.LAYOUT-MANAGER.NEW-OBJECT.IMAGE.LABEL',
            cssClass: "new-objects",
            icon: "cms/icon-image.png",
            description: 'CMS.LAYOUT-MANAGER.NEW-OBJECT.IMAGE.DESCRIPTION',
            group: {
                id: 2
            }
        };

        ContentObjectRegistryService.register(obj, objProperties);
    }]);
}